import { create } from "zustand";

interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
interface PwaState {
  online: boolean;
  installed: boolean;
  installPrompt: InstallPrompt | null;
  update: ServiceWorkerRegistration | null;
  error: boolean;
}
export const usePwa = create<PwaState>(() => ({ online: typeof navigator === "undefined" || navigator.onLine, installed: false, installPrompt: null, update: null, error: false }));

export function startPwa({ registerWorker = import.meta.env.PROD, reload = () => window.location.reload() } = {}) {
  const standalone = window.matchMedia("(display-mode: standalone)");
  let registration: ServiceWorkerRegistration | undefined;
  let requestedReload = false;
  let disposed = false;
  const syncDisplay = () => usePwa.setState({ installed: standalone.matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone) });
  const syncConnection = () => usePwa.setState({ online: navigator.onLine });
  const captureInstall = (event: Event) => { event.preventDefault(); usePwa.setState({ installPrompt: event as InstallPrompt }); };
  const installed = () => usePwa.setState({ installed: true, installPrompt: null });
  const reloadExplicitly = () => { requestedReload = false; usePwa.setState({ update: null }); reload(); };
  const controllerChanged = () => { if (requestedReload) reloadExplicitly(); };
  const requestReload = () => {
    // Another tab may already have activated the waiting worker. The current
    // document still needs the user's explicit reload, even with no waiter.
    if (!usePwa.getState().update?.waiting) reloadExplicitly();
    else requestedReload = true;
  };
  syncDisplay(); syncConnection();
  standalone.addEventListener?.("change", syncDisplay);
  window.addEventListener("online", syncConnection);
  window.addEventListener("offline", syncConnection);
  window.addEventListener("beforeinstallprompt", captureInstall);
  window.addEventListener("appinstalled", installed);
  window.addEventListener("conversa:pwa-update", requestReload);

  const checkWaiting = () => {
    if (registration?.waiting && navigator.serviceWorker.controller && !disposed) usePwa.setState({ update: registration });
  };
  const updateFound = () => {
    const worker = registration?.installing;
    worker?.addEventListener("statechange", () => { if (worker.state === "installed") checkWaiting(); });
  };
  const register = async () => {
    if (!registerWorker || !window.isSecureContext || !("serviceWorker" in navigator)) return;
    try {
      registration = await navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" });
      if (disposed) return;
      registration.addEventListener("updatefound", updateFound);
      navigator.serviceWorker.addEventListener("controllerchange", controllerChanged);
      checkWaiting(); updateFound();
    } catch { if (!disposed) usePwa.setState({ error: true }); }
  };
  if (document.readyState === "complete") void register();
  else window.addEventListener("load", register, { once: true });

  return () => {
    disposed = true;
    standalone.removeEventListener?.("change", syncDisplay);
    window.removeEventListener("online", syncConnection);
    window.removeEventListener("offline", syncConnection);
    window.removeEventListener("beforeinstallprompt", captureInstall);
    window.removeEventListener("appinstalled", installed);
    window.removeEventListener("conversa:pwa-update", requestReload);
    window.removeEventListener("load", register);
    registration?.removeEventListener("updatefound", updateFound);
    if ("serviceWorker" in navigator) navigator.serviceWorker.removeEventListener("controllerchange", controllerChanged);
  };
}

export async function installPwa() {
  const prompt = usePwa.getState().installPrompt;
  if (!prompt) return false;
  usePwa.setState({ installPrompt: null });
  try { await prompt.prompt(); await prompt.userChoice; return true; }
  catch { return false; }
}

export function applyPwaUpdate() {
  const registration = usePwa.getState().update;
  if (!registration) return;
  const worker = registration.waiting;
  window.dispatchEvent(new Event("conversa:pwa-update"));
  worker?.postMessage({ type: "ACTIVATE_UPDATE" });
}
